import { useLocation, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  Button,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
  withStyles,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import NotificationsIcon from "@material-ui/icons/Notifications";
import AlarmIcon from "@material-ui/icons/Alarm";
import CachedIcon from "@material-ui/icons/Cached";
import api from "../services/api";
import MainListItems from "./MainListItems";
import MobileBottomNav from "../components/MobileBottomNav";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import BirthdayModal from "../components/BirthdayModal";
import ChatPopover from "../pages/Chat/ChatPopover";
import { useDate } from "../hooks/useDate";
import ColorModeContext from "../layout/themeContext";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import { getBackendUrl } from "../config";
import useSettings from "../hooks/useSettings";
import useWhatsApps from "../hooks/useWhatsApps";
import VersionControl from "../components/VersionControl";
import useSocketListener from "../hooks/useSocketListener";
import { FaGlobe } from "react-icons/fa";
import { estratizaColors, estratizaGradients } from "../styles/estratizaTheme";
import { hasRolloutFeature, ROLLOUT_FEATURES } from "../utils/rolloutFeatures";

const backendUrl = getBackendUrl();
// Mantem rotulos e submenus inteiros. Os itens usam icone, margens e padding
// dimensionados para a largura historica do layout Estratiza.
const drawerWidth = 264;


const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh", // fallback
    // @ts-ignore
    height: "var(--vvh, 100dvh)",
    [theme.breakpoints.down("sm")]: {
      height: "100dvh",
    },
    backgroundColor: theme.palette.fancyBackground,
    "& .MuiDrawer-paper": {
      backgroundColor: `${estratizaColors.black} !important`,
      backgroundImage: `${estratizaGradients.sidebar} !important`,
      color: `${estratizaColors.goldLight} !important`,
    },
    "& .MuiButton-outlinedPrimary": {
      color: theme.palette.primary.main, // Usa cor do tema
      border: `1px solid ${theme.palette.primary.main}40`,
      borderRadius: "8px",
      fontWeight: 600,
      textTransform: "none",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: `${theme.palette.primary.main}10`,
        borderColor: theme.palette.primary.main,
        transform: "translateY(-1px)",
        boxShadow: `0 4px 12px ${theme.palette.primary.main}30`,
      },
    },
    "& .MuiTab-textColorPrimary.Mui-selected": {
      color: theme.palette.primary.main, // Usa cor do tema
      fontWeight: 700,
    },
  },

  chip: {
    background: "red",
    color: "white",
  },

  avatar: {
    width: "100%",
  },

  toolbar: {
    paddingRight: 24,
    paddingLeft: 8,
    color: "#ffffff",
    background: estratizaGradients.topbar,
    borderBottom: "1px solid rgba(212,175,55,0.28)",
    boxShadow: "0 10px 34px rgba(0,0,0,0.34)",
    paddingTop: "env(safe-area-inset-top)",
    minHeight: "calc(64px + env(safe-area-inset-top))",
  },

  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 8px",
    minHeight: "64px",
    [theme.breakpoints.down("sm")]: {
      height: "58px",
      paddingTop: "env(safe-area-inset-top)",
      minHeight: "calc(58px + env(safe-area-inset-top))",
    },
    background: estratizaGradients.sidebar,
    borderBottom: "1px solid rgba(212,175,55,0.20)",
    position: "relative",
    "&:after": {
      content: '""',
      position: "absolute",
      left: 18,
      right: 18,
      bottom: -1,
      height: 1,
      background: "linear-gradient(90deg, transparent, #D4AF37, transparent)",
    },
  },

  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  menuButtonHidden: {
    display: "none",
  },

  title: {
    flexGrow: 1,
    fontSize: 14,
    color: "#F7F0DF",
    fontWeight: 600,
    letterSpacing: "0.035em",
  },

  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
    overflowY: "hidden",
    backgroundColor: `${estratizaColors.black} !important`,
    backgroundImage: `${estratizaGradients.sidebar} !important`,
    color: estratizaColors.goldLight,
    borderRight: "1px solid rgba(212,175,55,0.22)",
    boxShadow: "14px 0 42px rgba(0,0,0,0.48)",
  },

  drawerPaperClose: {
    overflowX: "hidden",
    overflowY: "hidden",
    backgroundColor: `${estratizaColors.black} !important`,
    backgroundImage: `${estratizaGradients.sidebar} !important`,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },

  appBarSpacer: {
    minHeight: "calc(64px + env(safe-area-inset-top))",
  },

  content: {
    flex: 1,
    overflow: "auto",
    padding: 0,
    margin: 0,
    minHeight: 0,
    "@media (max-width: 767px)": {
      paddingBottom: "calc(60px + env(safe-area-inset-bottom))",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    ".mobile-chat-open &": {
      paddingBottom: "0 !important",
    },
  },

  container: {
    padding: 0,
    margin: 0,
    maxWidth: "none",
    width: "100%",
  },

  containerWithScroll: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    borderRadius: "8px",
    border: "2px solid transparent",
    backgroundColor: "transparent !important",
    color: estratizaColors.goldLight,
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(212,175,55,0.62) rgba(255,255,255,0.025)",
    "&::-webkit-scrollbar": {
      width: 7,
    },
    "&::-webkit-scrollbar-track": {
      background: "rgba(255,255,255,0.025)",
      borderRadius: 999,
    },
    "&::-webkit-scrollbar-thumb": {
      background: "linear-gradient(180deg, #F2D675, #9B7118)",
      borderRadius: 999,
      border: "2px solid #080705",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#F2D675",
    },
  },

  NotificationsPopOver: {
    // Mantém original
  },

  logo: {
    width: "100%",
    height: "45px",
    maxWidth: 180,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      height: "100%",
      maxWidth: 180,
    },
    logo: theme.logo,
    content:
      "url(" +
      (theme.mode === "light"
        ? theme.calculatedLogoLight()
        : theme.calculatedLogoDark()) +
      ")",
    transition: "all 0.3s ease",
    backgroundColor: "transparent",
    mixBlendMode: "normal",
    filter: "drop-shadow(0 8px 18px rgba(212,175,55,0.16))",
    "&:hover": {
      transform: "scale(1.02)",
    },
  },

  hideLogo: {
    display: "none",
  },

  avatar2: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    cursor: "pointer",
    borderRadius: "50%",
    border: `2px solid ${estratizaColors.gold}`,
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      borderColor: theme.palette.primary.main, // Usa cor do tema
    },
  },

  updateDiv: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  // Botões da toolbar melhorados
  toolbarButton: {
    color: "rgba(255, 255, 255, 0.9)",
    borderRadius: "8px",
    padding: "8px",
    margin: "0 2px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(212,175,55,0.14)",
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "translateY(0)",
    },
  },

  // Menu hambúrguer com animação sutil
  menuButton: {
    color: estratizaColors.goldLight,
    "&:hover": {
      backgroundColor: "rgba(212,175,55,0.14)",
    },
    "& .MuiSvgIcon-root": {
      transition: "transform 0.3s ease",
    },
    "&:hover .MuiSvgIcon-root": {
      transform: "rotate(90deg)",
    },
  },

  // Seletor de idioma melhorado
  languageSelector: {
    position: "relative",
    display: "inline-block",
    "& > button": {
      background: "rgba(255, 255, 255, 0.1)",
      border: "none",
      borderRadius: "8px",
      color: "rgba(255, 255, 255, 0.9)",
      fontSize: "18px",
      padding: "8px 12px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      "&:hover": {
        background: "rgba(255, 255, 255, 0.2)",
        transform: "translateY(-1px)",
      },
    },
    "& > div": {
      position: "absolute",
      top: "45px",
      left: "0",
      background: "#fff",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      borderRadius: "8px",
      padding: "8px",
      zIndex: 1000,
      minWidth: "120px",
      "& button": {
        background: "none",
        border: "none",
        color: "#374151",
        display: "block",
        width: "100%",
        padding: "8px 12px",
        textAlign: "left",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: 500,
        transition: "all 0.2s ease",
        "&:hover": {
          background: `${theme.palette.primary.main}10`, // Usa cor do tema
          color: theme.palette.primary.main, // Usa cor do tema
          transform: "none",
        },
      },
    },
  },

  // Badge animado
  animatedBadge: {
    "& .MuiBadge-badge": {
      animation: "$heartbeat 2s infinite",
    },
  },

  "@keyframes heartbeat": {
    "0%": { transform: "scale(1)" },
    "14%": { transform: "scale(1.1)" },
    "28%": { transform: "scale(1)" },
    "42%": { transform: "scale(1.1)" },
    "70%": { transform: "scale(1)" },
  },
  reminderPulse: {
    animation: "$reminderPulse 1.15s ease-in-out infinite",
    borderRadius: "50%",
  },
  "@keyframes reminderPulse": {
    "0%": { transform: "scale(1)", filter: "drop-shadow(0 0 0 rgba(239,68,68,0))" },
    "50%": { transform: "scale(1.22)", filter: "drop-shadow(0 0 7px rgba(239,68,68,0.95))" },
    "100%": { transform: "scale(1)", filter: "drop-shadow(0 0 0 rgba(239,68,68,0))" },
  },
}));

const StyledBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "$ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}))(Badge);

const SmallAvatar = withStyles((theme) => ({
  root: {
    width: 22,
    height: 22,
    border: `2px solid ${theme.palette.background.paper}`,
  },
}))(Avatar);

const LoggedInLayout = ({ children, themeToggle, hideMenu = false }) => {
  const classes = useStyles();
  const location = useLocation();
  const isMobileChat = /^\/tickets\/\S+/.test(location.pathname);
  const isEmbedded = new URLSearchParams(location.search).get('embed') === '1';
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, isAuth, user, socket } = useContext(AuthContext);
  const remindersV2 = hasRolloutFeature(user, ROLLOUT_FEATURES.REMINDERS_V2);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Monitorar status das conexoes do usuario logado
  const { whatsApps } = useWhatsApps();
  const userDisconnectedConns = React.useMemo(() => {
    if (!user?.whatsapps || !Array.isArray(user.whatsapps)) return [];
    const myIds = user.whatsapps.map(w => w.id);
    return (whatsApps || []).filter(w =>
      myIds.includes(w.id) && w.status !== "CONNECTED"
    );
  }, [whatsApps, user?.whatsapps]);

  // AI token limit — estado persistente
  const [aiLimitReached, setAiLimitReached] = useState(false);
  const [aiBlockReason, setAiBlockReason] = useState("limit");

  // Checa ao montar se empresa ja esta com limite atingido
  useEffect(() => {
    if (!user?.companyId) return;
    api.get(`/companies/${user.companyId}`).then(({ data }) => {
      const limit = data?.plan?.aiTokensLimit || 0;
      const used = data?.aiTokensUsed || 0;
      if (limit > 0 && used >= limit) {
        setAiLimitReached(true);
        setAiBlockReason("limit");
      }
    }).catch(() => {});
  }, [user?.companyId]);

  // AI token limit notification — socket em tempo real
  useEffect(() => {
    if (!user?.companyId || !socket) return;
    const eventName = `company-${user.companyId}-ai-token-limit`;
    const handler = (data) => {
      if (data.action === "blocked") {
        setAiLimitReached(true);
        const reason = data.reason === "configuration" ? "configuration" : "limit";
        setAiBlockReason(reason);
        toast.error(
          reason === "configuration"
            ? "A IA está pausada porque o contrato ou plano da empresa precisa ser configurado. Entre em contato com o suporte da Estratiza."
            : "Limite de tokens de IA atingido. A inteligência artificial foi pausada. Entre em contato para fazer upgrade do plano.",
          { autoClose: 10000, toastId: "ai-token-limit" }
        );
      }
    };
    socket.on(eventName, handler);
    return () => { socket.off(eventName, handler); };
  }, [user?.companyId, socket]);
  const [drawerVariant, setDrawerVariant] = useState("permanent");

  const [showOptions, setShowOptions] = useState(false);
  const [showAnnouncementsModal, setShowAnnouncementsModal] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState(() => {
    try {
      const stored = localStorage.getItem("announcementsDismissed");
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));

  const [volume, setVolume] = useState(parseFloat(localStorage.getItem("volume") ?? "1"));

  const { dateToClient, returnDays } = useDate();
  const [profileUrl, setProfileUrl] = useState(null);
  const [updateInProgress, setUpdateInProgress] = useState(false);

  const handleDismissAnnouncement = (ann) => {
    const updatedAtKey = ann.updatedAt || ann.createdAt;
    setDismissedAnnouncements(prev => {
      const updated = { ...prev, [ann.id]: updatedAtKey };
      try { localStorage.setItem("announcementsDismissed", JSON.stringify(updated)); } catch {}
      return updated;
    });
    // Remove da lista visível
    setAnnouncements(prev => prev.filter(a => a.id !== ann.id));
    setSelectedAnnouncement(null);
    // Se não sobrar nenhum, fecha o modal
    const remaining = announcements.filter(a => a.id !== ann.id);
    if (remaining.length === 0) {
      setShowAnnouncementsModal(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mainListItems = useMemo(
    () => <MainListItems drawerOpen={drawerOpen} collapsed={!drawerOpen} />,
    [user, drawerOpen]
  );

  const settings = useSettings();

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const { data } = await api.get("/announcements/for-company", {
          params: {
            status: true,
            pageNumber: "1"
          }
        });

        // Filtra apenas os informativos ativos e não expirados
        const activeAnnouncements = data.records.filter(announcement => {
          const isActive = announcement.status === true || announcement.status === "true";
          const isNotExpired = !announcement.expiresAt || new Date(announcement.expiresAt) > new Date();
          return isActive && isNotExpired;
        });

        setAnnouncements(activeAnnouncements);

        // Filtra informativos não dispensados
        let storedDismissed = {};
        try {
          const stored = localStorage.getItem("announcementsDismissed");
          storedDismissed = stored ? JSON.parse(stored) : {};
        } catch {}
        
        const visibleAnnouncements = activeAnnouncements.filter(ann => {
          const dismissedAt = storedDismissed[ann.id];
          if (!dismissedAt) return true;
          return ann.updatedAt && ann.updatedAt !== dismissedAt;
        });

        // Mostra o modal apenas se houver informativos visíveis
        if (visibleAnnouncements.length > 0) {
          setShowAnnouncementsModal(true);
        }
      } catch (err) {
        toastError(err);
      }
    };

    if (user?.id) {
      fetchAnnouncements();
    }
  }, [user?.id]);

  useEffect(() => {
    // if (localStorage.getItem("public-token") === null) {
    //   handleLogout()
    // }

    if (document.body.offsetWidth > 600) {
      setDrawerOpen(true);
    } else {
      setDrawerOpen(false);
    }
    if (user.defaultTheme === "dark" && theme.mode === "light") {
      colorMode.toggleColorMode();
    }
  }, [user.defaultMenu, document.body.offsetWidth]);

  // Mobile bottom nav: abrir menu de usuario ao tocar no hamburguer
  useEffect(() => {
    const handler = () => setMobileMenuOpen(true);
    window.addEventListener("mobile-open-menu", handler);
    return () => window.removeEventListener("mobile-open-menu", handler);
  }, []);

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  useEffect(() => {
    const companyId = user?.companyId;

    if (companyId) {
      const buildProfileUrl = () => {
        const savedProfileImage = localStorage.getItem("profileImage");
        const currentProfileImage = savedProfileImage || user.profileImage;

        if (currentProfileImage) {
          return `${backendUrl}/public/company${companyId}/user/${currentProfileImage}`;
        }
        return `${backendUrl}/public/app/noimage.png`;
      };

      setProfileUrl(buildProfileUrl());
    }
  }, [user?.companyId, user?.profileImage, backendUrl]);

  // Callbacks dos eventos
  const handleAuthEvent = useCallback((data) => {
    if (data.user.id === +user?.id) {
      toastError("Sua conta foi acessada em outro computador.");
      setTimeout(() => {
        localStorage.clear();
        window.location.reload();
      }, 1000);
    }
  }, [user?.id]);

  const handleUserUpdate = useCallback((data) => {
    if (data.action === "update" && data.user.id === +user?.id) {
      if (data.user.profileImage) {
        const newProfileUrl = `${backendUrl}/public/company${user?.companyId}/user/${data.user.profileImage}`;
        setProfileUrl(newProfileUrl);
        localStorage.setItem("profileImage", data.user.profileImage);
      }
    }
  }, [user?.companyId, user?.id, backendUrl]);

  // Callbacks para eventos de aniversário
  const handleUserBirthday = useCallback((data) => {
    console.log("🎂 Evento de aniversário de usuário recebido:", data);
    if (data.userId === +user?.id) {
      setShowBirthdayModal(true);
    }
  }, [user?.id]);

  const handleContactBirthday = useCallback((data) => {
    console.log("🎂 Evento de aniversário de contato recebido:", data);
    // Para contatos, apenas logamos por enquanto
    // A mensagem já foi enviada automaticamente pelo backend
  }, []);

  // Verificar aniversários no login
  const checkBirthdaysOnLogin = useCallback(async () => {
    if (user?.id && user?.companyId) {
      try {
        const { data } = await api.get("/birthdays/today");
        const birthdayData = data.data;

        // Verificar se o usuário atual faz aniversário hoje
        const userBirthday = birthdayData.users.find(u => u.id === +user.id);
        if (userBirthday) {
          console.log("🎂 Usuário faz aniversário hoje! Mostrando modal...");
          setShowBirthdayModal(true);
        }

        // Se há aniversariantes, mostrar notificação
        if (birthdayData.users.length > 0 || birthdayData.contacts.length > 0) {
          console.log("🎂 Há aniversariantes hoje:", birthdayData);
        }
      } catch (error) {
        console.error("Erro ao verificar aniversários:", error);
      }
    }
  }, [user?.id, user?.companyId]);

  // Registrar listeners
  useSocketListener(socket, user, 'auth', handleAuthEvent);

  // Lembretes
  const [dueReminders, setDueReminders] = useState([]);
  const historyNav = useHistory();

  // Presenca efemera: evita push redundante quando o proprio usuario esta
  // olhando o mesmo ticket. Expira no backend se o heartbeat parar.
  useEffect(() => {
    if (!user?.id || !user?.companyId) return undefined;
    const match = location.pathname.match(/^\/tickets\/([^/?#]+)/);
    const ticketUuid = match ? match[1] : null;
    const reportPresence = (visible = document.visibilityState === "visible") => {
      api.post("/push/presence", {
        ticketUuid,
        visible: Boolean(visible && ticketUuid)
      }).catch(() => {});
    };

    reportPresence();
    const intervalId = window.setInterval(reportPresence, 25000);
    const handleVisibility = () => reportPresence();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibility);
      api.post("/push/presence", {
        ticketUuid: null,
        visible: false
      }).catch(() => {});
    };
  }, [location.pathname, user?.id, user?.companyId]);

  // Fix mobile keyboard: ajusta altura do layout quando teclado virtual abre/fecha
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const isIosViewport =
      /iPad|iPhone|iPod/i.test(window.navigator.userAgent) ||
      (window.navigator.platform === "MacIntel" &&
        window.navigator.maxTouchPoints > 1);
    let largestViewportHeight = vv.height;
    const handleResize = () => {
      const availableHeight = vv.height;
      const offsetTop = vv.offsetTop || 0;
      if (availableHeight > largestViewportHeight) {
        largestViewportHeight = availableHeight;
      }
      const keyboardOpen =
        isMobileChat && largestViewportHeight - availableHeight > 120;
      document.documentElement.style.setProperty('--vvh', availableHeight + 'px');
      // offsetTop acompanha somente o Visual Viewport. Somar scrollY/pageTop
      // fazia o composer descer repetidamente enquanto o Safari mantinha o
      // textarea em foco.
      document.documentElement.style.setProperty('--vvy', offsetTop + 'px');
      document.documentElement.style.setProperty(
        '--composer-safe-bottom',
        keyboardOpen ? '0px' : 'env(safe-area-inset-bottom)'
      );
      document.documentElement.toggleAttribute(
        'data-mobile-keyboard-open',
        keyboardOpen
      );
      document.documentElement.toggleAttribute(
        'data-ios-keyboard-open',
        isIosViewport && keyboardOpen
      );
    };
    vv.addEventListener('resize', handleResize);
    vv.addEventListener('scroll', handleResize);
    window.addEventListener('scroll', handleResize, { passive: true });
    handleResize();
    return () => {
      vv.removeEventListener('resize', handleResize);
      vv.removeEventListener('scroll', handleResize);
      window.removeEventListener('scroll', handleResize);
      document.documentElement.removeAttribute('data-mobile-keyboard-open');
      document.documentElement.removeAttribute('data-ios-keyboard-open');
      document.documentElement.style.removeProperty('--composer-safe-bottom');
    };
  }, [isMobileChat]);

  // Listener para clique em push notification (service worker envia postMessage)
  useEffect(() => {
    const handleSWMessage = (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data?.url) {
        localStorage.setItem("estratizaPushTarget", event.data.url);
        if (isAuth && !loading) {
          historyNav.push(event.data.url);
        }
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleSWMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
  }, [historyNav, isAuth, loading]);

  // Se o clique no push passou pela tela de login, a rota privada preserva
  // o destino; este fallback garante a continuidade apos autenticar.
  useEffect(() => {
    if (loading || !isAuth) return;
    const target = localStorage.getItem("estratizaPushTarget");
    if (target && /^\/tickets\/[A-Za-z0-9-]+$/.test(target)) {
      localStorage.removeItem("estratizaPushTarget");
      historyNav.push(target);
    }
  }, [historyNav, isAuth, loading]);

  const refreshDueReminders = useCallback(async () => {
    if (!user?.id || !user?.companyId) return;
    try {
      const res = await api.get('/reminders');
      const notified = (res.data || []).filter(
        r => r.status === 'notified' && String(r.userId) === String(user.id)
      );
      setDueReminders(notified);
    } catch (_) {}
  }, [user?.id, user?.companyId]);

  useEffect(() => {
    refreshDueReminders();
    if (!remindersV2) return undefined;
    const intervalId = window.setInterval(refreshDueReminders, 30000);
    return () => window.clearInterval(intervalId);
  }, [refreshDueReminders, remindersV2]);

  const viewReminder = useCallback(async reminder => {
    if (!reminder) return;
    if (!remindersV2) {
      if (reminder.ticketId) historyNav.push(`/tickets/${reminder.ticketId}`);
      else historyNav.push('/reminders');
      return;
    }
    try {
      await api.post(`/reminders/${reminder.id}/ack`);
      setDueReminders(prev => prev.filter(item => item.id !== reminder.id));
      historyNav.push('/reminders');
    } catch (err) {
      toastError(err);
    }
  }, [historyNav, remindersV2]);

  const closeReminder = useCallback(async reminder => {
    if (!remindersV2 || !reminder) return;
    try {
      await api.post(`/reminders/${reminder.id}/ack`);
      setDueReminders(prev => prev.filter(item => item.id !== reminder.id));
    } catch (err) {
      toastError(err);
    }
  }, [remindersV2]);

  // Tocar som de alerta via Web Audio API (respeita volume do slider, resume contexto suspenso)
  const playReminderSound = () => {
    const vol = parseFloat(localStorage.getItem("volume") ?? "1");
    if (isNaN(vol) || vol <= 0) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const doPlay = () => {
        [0, 0.15, 0.3].forEach(delay => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.value = 880;
          gain.gain.setValueAtTime(vol * 0.4, ctx.currentTime + delay);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.3);
          osc.start(ctx.currentTime + delay);
          osc.stop(ctx.currentTime + delay + 0.3);
        });
      };
      if (ctx.state === 'suspended') {
        ctx.resume().then(doPlay);
      } else {
        doPlay();
      }
    } catch(e) {}
  };

  useSocketListener(socket, user, 'reminder', (data) => {
    if (data.action === 'due' && String(data.reminder?.userId) === String(user?.id)) {
      setDueReminders(prev => {
        const alreadyIn = prev.some(r => r.id === data.reminder.id);
        return alreadyIn ? prev : [...prev, data.reminder];
      });
      playReminderSound();
      if ('Notification' in window && Notification.permission === 'granted') {
        const n = new Notification('🔔 Lembrete: ' + data.reminder.title, {
          body: data.reminder.notes || 'Clique para abrir',
          icon: '/favicon.ico',
          requireInteraction: true,
        });
        n.onclick = () => {
          window.focus();
          viewReminder(data.reminder);
        };
      }
    }
  });
  useSocketListener(socket, user, 'user', handleUserUpdate);
  useSocketListener(socket, user, 'user-birthday', handleUserBirthday);
  useSocketListener(socket, user, 'contact-birthday', handleContactBirthday);

  // Verificar aniversários quando o usuário faz login
  useEffect(() => {
    if (user?.id && user?.companyId) {
      // Pequeno delay para garantir que o socket esteja conectado
      const timer = setTimeout(() => {
        checkBirthdaysOnLogin();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [user?.id, user?.companyId, checkBirthdaysOnLogin]);

  // Status do usuário
  useEffect(() => {
    if (socket?.emit && user?.companyId) {
      socket.emit("userStatus");

      const interval = setInterval(() => {
        socket?.emit && socket.emit("userStatus");
      }, 1000 * 60 * 5);

      return () => clearInterval(interval);
    }
  }, [socket, user?.companyId]);

  const handleUpdateStart = () => {
    setUpdateInProgress(true);
  };

  const handleUpdateComplete = () => {
    setUpdateInProgress(false);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload(false);
  };

  const handleMenuItemClick = () => {
    const { innerWidth: width } = window;
    if (width <= 600) {
      setDrawerOpen(false);
    }
  };

  const handleLanguageChange = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
    window.location.reload();
  };

  const LANGUAGE_OPTIONS = [
    { code: "pt-BR", label: "Português" },
    { code: "en", label: "English" },
    { code: "es", label: "Spanish" },
    { code: "ar", label: "عربي" },
  ];

  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
  const { getAll } = useSettings();
  useEffect(() => {
    async function fetchSettings() {
      try {
        const settings = await getAll();
        const enabledLanguagesSetting = settings.find(
          (s) => s.key === "enabledLanguages"
        )?.value;
        let langs = ["pt-BR", "en"];
        try {
          if (enabledLanguagesSetting) {
            langs = JSON.parse(enabledLanguagesSetting);
          }
        } catch { }
        console.log(
          "Layout - enabledLanguages carregadas:",
          langs,
          "para companyId:",
          user?.companyId
        );
        setEnabledLanguages(langs);
      } catch (error) {
        console.log("Layout - erro ao carregar enabledLanguages:", error);
      }
    }
    fetchSettings();
  }, [user?.companyId]);

  const filteredLanguageOptions = LANGUAGE_OPTIONS.filter((lang) =>
    enabledLanguages.includes(lang.code)
  );

  if (loading || updateInProgress) {
    return <BackdropLoading />;
  }

  // Modo embutido (iframe): renderiza só o conteúdo sem sidebar/topbar
  if (isEmbedded || hideMenu) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        {children ? children : null}
      </div>
    );
  }

  return (
    <div className={clsx(classes.root, "logged-in-layout")}>
      {!hideMenu && greaterThenSm && (
        <Drawer
          variant={drawerVariant}
          className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
          classes={{
            paper: clsx(
              classes.drawerPaper,
              !drawerOpen && classes.drawerPaperClose
            ),
          }}
          open={drawerOpen}
        >
          <div className={classes.toolbarIcon}>
            <img
              className={drawerOpen ? classes.logo : classes.hideLogo}
              src={
                theme.mode === "dark"
                  ? theme.calculatedLogoDark()
                  : theme.calculatedLogoLight()
              }
              style={{
                display: "block",
                margin: "0 auto",
                height: "50px",
                width: "100%",
                backgroundColor: "transparent",
                objectFit: "contain",
                mixBlendMode: "normal",
              }}
              alt="logo"
            />
            <IconButton onClick={() => setDrawerOpen(!drawerOpen)} style={{ color: estratizaColors.goldLight }}>
              <ChevronLeftIcon />
            </IconButton>
          </div>
          <List className={classes.containerWithScroll}>
            {/* {mainListItems} */}
            <MainListItems collapsed={!drawerOpen} />
          </List>
          <Divider />
        </Drawer>
      )}

      {greaterThenSm && (
      <AppBar
        position="absolute"
        className={clsx(classes.appBar, !hideMenu && drawerOpen && greaterThenSm && classes.appBarShift)}
        color="primary"
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          {!hideMenu && (
            <IconButton
              edge="start"
              variant="contained"
              aria-label="open drawer"
              style={{ color: estratizaColors.goldLight }}
              onClick={() => setDrawerOpen(!drawerOpen)}
              className={clsx(drawerOpen && greaterThenSm && classes.menuButtonHidden)}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            component="h2"
            variant="h6"
            color="inherit"
            noWrap
            className={classes.title}
          >
            {greaterThenSm && (
              <>
                <b style={{ marginRight: 4 }}>{user?.company?.name}</b>
                {" · Bem-vindo ao Estratiza Hub"}
                {user?.company?.dueDate ? (() => {
                  const daysLeft = returnDays(user?.company?.dueDate);
                  const isCritical = daysLeft <= 3;
                  const isWarning = !isCritical && daysLeft <= 5;
                  if (isCritical) {
                    const label = daysLeft < 0
                      ? `vencido ha ${Math.abs(daysLeft)} dia${Math.abs(daysLeft) === 1 ? "" : "s"}`
                      : daysLeft === 0
                        ? "vence hoje"
                        : `vence em ${daysLeft} dia${daysLeft === 1 ? "" : "s"}`;
                    return (
                      <span style={{ marginLeft: 6, fontWeight: 700, fontSize: "0.78em", padding: "2px 9px", borderRadius: 100, backgroundColor: "#FDEDEC", color: "#C6362E" }}>
                        {label}
                      </span>
                    );
                  }
                  if (isWarning) {
                    return (
                      <span style={{ marginLeft: 6, fontWeight: 600, fontSize: "0.78em", padding: "2px 9px", borderRadius: 100, backgroundColor: "#FCF3E1", color: "#B9790A" }}>
                        {`vence em ${daysLeft} dias`}
                      </span>
                    );
                  }
                  return (
                    <span style={{ marginLeft: 6, opacity: 0.75, fontWeight: 400, fontSize: "0.85em" }}>
                      (plano ativo até {dateToClient(user?.company?.dueDate)})
                    </span>
                  );
                })() : null}
              </>
            )}
          </Typography>

          {!hideMenu && (
            <>
              {/* VersionControl desativado */}

              <div
                style={{ position: "relative", display: "inline-block" }}
                className="language-dropdown"
              >
                <button
                  onClick={() => setShowOptions(!showOptions)}
                  style={{
                    background: "none",
                    border: "none",
                    color: estratizaColors.goldLight,
                    cursor: "pointer",
                    fontSize: "22px",
                    paddingRight: "20px",
                    paddingTop: "8px",
                  }}
                >
                  <FaGlobe />
                </button>

                {showOptions && (
                  <div
                    style={{
                      position: "absolute",
                      top: "35px",
                      left: "0",
                      background: "#fff",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                      borderRadius: "8px",
                      padding: "8px",
                      zIndex: 1000,
                      minWidth: "120px",
                      maxWidth: "200px",
                    }}
                  >
                    {filteredLanguageOptions.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          display: "block",
                          width: "100%",
                          padding: "4px",
                        }}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <IconButton edge="start" onClick={colorMode.toggleColorMode}>
                {theme.mode === "dark" ? (
                  <Brightness7Icon style={{ color: estratizaColors.goldLight }} />
                ) : (
                  <Brightness4Icon style={{ color: estratizaColors.goldLight }} />
                )}
              </IconButton>

              <NotificationsVolume setVolume={setVolume} volume={volume} />

              <IconButton
                onClick={handleRefreshPage}
                aria-label={i18n.t("mainDrawer.appBar.refresh")}
                color="inherit"
              >
                <CachedIcon style={{ color: estratizaColors.goldLight }} />
              </IconButton>

              {/* <DarkMode themeToggle={themeToggle} /> */}

              {user.id && <NotificationsPopOver volume={volume} />}

              {/* Lembretes com badge */}
              <Badge badgeContent={dueReminders.length} color="error" overlap="rectangular"
                className={remindersV2 && dueReminders.length > 0 ? classes.reminderPulse : undefined}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  if (!remindersV2) setDueReminders([]);
                  historyNav.push("/reminders");
                }}>
                <AlarmIcon style={{ color: dueReminders.length > 0 ? "#ef4444" : estratizaColors.goldLight, fontSize: 22 }} />
              </Badge>

              <AnnouncementsPopover />

              <ChatPopover />



              <div className="user-menu-wrapper">
                <StyledBadge
                  overlap="circular"
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  variant="dot"
                  onClick={handleMenu}
                >
                  <Avatar
                    alt="Multi100"
                    className={classes.avatar2}
                    src={profileUrl}
                  />
                </StyledBadge>

                <UserModal
                  open={userModalOpen}
                  onClose={() => setUserModalOpen(false)}
                  onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
                  userId={user?.id}
                />

                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  getContentAnchorEl={null}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={menuOpen}
                  onClose={handleCloseMenu}
                  PaperProps={{
                    style: {
                      minWidth: "150px",
                      maxWidth: "200px",
                      width: "auto",
                    },
                  }}
                >
                  <MenuItem onClick={handleOpenUserModal}>
                    {i18n.t("mainDrawer.appBar.user.profile")}
                  </MenuItem>
                  <MenuItem onClick={handleClickLogout}>
                    {i18n.t("mainDrawer.appBar.user.logout")}
                  </MenuItem>
                </Menu>
              </div>
            </>
          )}
        </Toolbar>
      </AppBar>
      )}
      <main className={classes.content} style={isMobileChat ? { paddingBottom: 0 } : undefined}>
        {greaterThenSm && <div className={classes.appBarSpacer} />}

        {remindersV2 && dueReminders.length > 0 && (
          <div style={{
            position: "sticky",
            top: 0,
            zIndex: 1300,
            background: "linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 18px",
            flexWrap: "wrap",
            gap: 12,
            boxShadow: "0 4px 14px rgba(127,29,29,0.38)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              <AlarmIcon className={classes.reminderPulse} style={{ fontSize: 28, flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 900, fontSize: 14, letterSpacing: "0.025em" }}>
                  VOCÊ TEM UM LEMBRETE PARA AGORA
                </div>
                <div style={{ fontWeight: 700, fontSize: 13, marginTop: 2 }}>
                  {dueReminders[0].title}
                  {dueReminders[0].contactName ? ` — ${dueReminders[0].contactName}` : ""}
                </div>
                <div style={{ fontSize: 12, opacity: 0.92, marginTop: 1 }}>
                  {new Date(dueReminders[0].dateTime).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  {dueReminders.length > 1 ? ` • mais ${dueReminders.length - 1} lembrete(s)` : ""}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              <Button
                size="small"
                variant="contained"
                onClick={() => viewReminder(dueReminders[0])}
                style={{ background: "#fff", color: "#991b1b", fontWeight: 800 }}
              >
                Ver lembrete
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => closeReminder(dueReminders[0])}
                style={{ borderColor: "rgba(255,255,255,0.8)", color: "#fff", fontWeight: 800 }}
              >
                Fechar
              </Button>
            </div>
          </div>
        )}

        {/* Banner fixo: conexao desconectada */}
        {userDisconnectedConns.length > 0 && (
          <div style={{
            position: "sticky",
            top: 0,
            zIndex: 1200,
            background: "#c62828",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 20px",
            flexWrap: "wrap",
            gap: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span style={{ fontWeight: 700, fontSize: 14 }}>
                {userDisconnectedConns.length === 1
                  ? `Sua conexão "${userDisconnectedConns[0].name}" está desconectada — você não consegue enviar mensagens.`
                  : `${userDisconnectedConns.length} conexões desconectadas — você não consegue enviar mensagens.`
                }
              </span>
            </div>
            <button
              onClick={() => window.location.href = "/connections"}
              style={{
                background: "#fff",
                color: "#c62828",
                border: "none",
                borderRadius: 6,
                padding: "6px 16px",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Conectar agora
            </button>
          </div>
        )}

        {/* Banner fixo: limite de IA atingido */}
        {aiLimitReached && (
          <div style={{
            position: "sticky",
            top: 0,
            zIndex: 1199,
            background: "#e65100",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 20px",
            flexWrap: "wrap",
            gap: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span style={{ fontWeight: 700, fontSize: 14 }}>
                {aiBlockReason === "configuration"
                  ? "A IA está pausada porque o contrato ou plano da empresa precisa ser configurado. Entre em contato com o suporte da Estratiza."
                  : "Limite de tokens de IA atingido — a inteligência artificial foi pausada. Entre em contato para fazer upgrade do plano."}
              </span>
            </div>
          </div>
        )}

        {children ? children : null}
      </main>

      {/* Modal de Informativos */}
      <Dialog
        open={showAnnouncementsModal}
        onClose={() => setShowAnnouncementsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Informativos</DialogTitle>
        <DialogContent dividers>
          {selectedAnnouncement ? (
            <div>
              <Typography variant="h6" gutterBottom>
                {selectedAnnouncement.title}
              </Typography>
              <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                {selectedAnnouncement.text}
              </Typography>
              {selectedAnnouncement.mediaPath && (
                <div style={{ marginTop: 16 }}>
                  <img
                    src={`${backendUrl}/public/company${user.companyId}${selectedAnnouncement.mediaPath}`}
                    alt="Anexo"
                    style={{ maxWidth: '100%' }}
                  />
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                <Button
                  onClick={() => handleDismissAnnouncement(selectedAnnouncement)}
                  color="default"
                  size="small"
                  style={{ fontSize: 12, textTransform: "none", color: "#888" }}
                >
                  Não exibir novamente
                </Button>
                <Button
                  onClick={() => setSelectedAnnouncement(null)}
                  variant="outlined"
                >
                  Voltar para lista
                </Button>
              </div>
            </div>
          ) : (
            <List>
              {announcements.map((announcement) => (
                <ListItem
                  button
                  key={announcement.id}
                  onClick={() => setSelectedAnnouncement(announcement)}
                >
                  <ListItemAvatar>
                    <Avatar>
                      <NotificationsIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={announcement.title}
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="textPrimary"
                        >
                          Prioridade: {announcement.priority === 1 ? 'Alta' : announcement.priority === 2 ? 'Média' : 'Baixa'}
                        </Typography>
                        {` — ${new Date(announcement.createdAt).toLocaleDateString()}`}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowAnnouncementsModal(false)}
            color="primary"
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Aniversário */}
      <BirthdayModal
        open={showBirthdayModal}
        onClose={() => setShowBirthdayModal(false)}
        user={user}
      />

      <MobileBottomNav />

      {/* Menu mobile (hamburguer) */}
      <Dialog
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        style={{ zIndex: 1500 }}
        PaperProps={{
          style: {
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            margin: 0,
            borderRadius: "16px 16px 0 0",
            width: "100%",
            maxWidth: "100%",
            background: estratizaGradients.sidebar,
            color: "#fff",
            paddingBottom: 70,
          }
        }}
      >
        <DialogContent style={{padding: 0}}>
          {/* Cabecalho do usuario */}
          <div style={{display:"flex", alignItems:"center", gap:12, padding:"20px 20px 12px"}}>
            <Avatar src={profileUrl} style={{width:44, height:44}} />
            <div>
              <div style={{fontWeight:600, fontSize:15}}>{user?.name}</div>
              <div style={{fontSize:12, opacity:0.6}}>{user?.company?.name}</div>
            </div>
          </div>
          <Divider style={{background:"rgba(255,255,255,0.1)"}} />
          <List style={{padding:"8px 0"}}>
            <ListItem
              button
              onClick={() => { setMobileMenuOpen(false); setUserModalOpen(true); }}
              style={{padding:"14px 20px", gap:14}}
            >
              <Avatar src={profileUrl} style={{width:32, height:32}} />
              <ListItemText primary="Meu Perfil" primaryTypographyProps={{style:{color:"#fff", fontWeight:500}}} />
            </ListItem>
            <ListItem
              button
              onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
              style={{padding:"14px 20px", gap:14}}
            >
              <div style={{width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center"}}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ff5252" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              </div>
              <ListItemText primary="Sair" primaryTypographyProps={{style:{color:"#ff5252", fontWeight:500}}} />
            </ListItem>
          </List>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoggedInLayout;
